// src/pages/User.js

import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, onSnapshot, deleteDoc, collection, query, where, getDocs, arrayUnion, orderBy, deleteField, arrayRemove, runTransaction } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { UserCircleIcon, CloudArrowUpIcon, PencilIcon, CheckIcon, XMarkIcon, ShieldCheckIcon, UserGroupIcon, BuildingOfficeIcon, PlusIcon, TrashIcon, DocumentTextIcon, UserIcon, BellIcon, LockClosedIcon, Cog6ToothIcon, ExclamationTriangleIcon, FunnelIcon, ChevronDownIcon, ChevronUpIcon, EnvelopeIcon, HeartIcon, ChatBubbleLeftRightIcon as ChatIcon } from '@heroicons/react/24/outline';
// NotificationPermission removed - web app uses email-only notifications
import { Switch } from '@headlessui/react';
import ImageUpload from '../components/ImageUpload';
import { Tab } from '@headlessui/react';
import { formatPhoneNumber, isValidPhoneNumber } from '../utils/phoneUtils';
import { useAuth } from '../contexts/authContext';
import { updateProfile } from 'firebase/auth';
import { uploadImage } from '../utils/imageUpload';
import EditStory from '../components/EditStory';
import StoryCard from '../components/StoryCard';
import StoriesBox from '../components/StoriesBox';
import { Dialog } from '@headlessui/react';
import TimezoneSelect from 'react-timezone-select';
import { sections } from '../data/journeyQuestions';
import { toast } from 'react-hot-toast';
// requestNotificationPermission removed - web app uses email-only notifications
import { analyzeJourneyResponses } from '../utils/journeyAnalysis';
import { GROWTH_AREA_COLORS } from '../utils/constants';
import Drawer from '../components/Drawer';
import MissionalScale from '../components/MissionalScale';
import ShareStory from '../components/ShareStory';

// Add this helper function at the top of the file, outside the component
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  if (timestamp.toDate) {
    return timestamp.toDate().toLocaleDateString();
  }
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString();
  }
  return new Date(timestamp).toLocaleDateString();
};

const UserProfileSkeleton = () => (
  <div className="p-6 bg-white">
    <div className="animate-pulse">
      {/* Profile Header Skeleton */}
      <div className="flex items-start space-x-4">
        <div className="h-24 w-24 bg-gray-200 rounded-full"></div>
        <div className="flex-1">
          <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 w-64 bg-gray-200 rounded"></div>
        </div>
      </div>
      
      {/* Tab Skeleton */}
      <div className="mt-8">
        <div className="flex space-x-4 border-b border-gray-200">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 w-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Add this helper function at the top
const compileJourneyStory = (responses) => {
  if (!responses) return '';
  
  let story = '';
  sections.forEach((section, sIndex) => {
    story += `\n\n## ${section.title}\n\n`;
    section.questions.forEach((question, qIndex) => {
      const response = responses[`${sIndex}_${qIndex}`];
      if (response && response.trim()) {
        story += `${response}\n\n`;
      }
    });
  });
  return story.trim();
};

// Helper function to combine responses into a paragraph
const combineResponses = (responses, sectionIndex) => {
  if (!responses) return '';
  
  // Get all responses for this section by matching the section prefix
  const sectionResponses = Object.entries(responses)
    .filter(([key]) => key.startsWith(`${sectionIndex}_`))
    .map(([_, value]) => value)
    .filter(response => response && response.trim());

  return sectionResponses.join(' ');
};

const User = () => {
  const navigate = useNavigate();
  const { userId } = useParams(); // This will be the appID format (like UID003)
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preferences, setPreferences] = useState({
    method: 'fcm',
    phoneNumber: '',
    enabled: true,
    quietHours: {
      start: '22:00',
      end: '08:00'
    }
  });
  const [isEditing, setIsEditing] = useState({
    name: false,
    email: false,
    phone: false,
    timezone: false,
    bio: false
  });
  const [editValues, setEditValues] = useState({
    name: '',
    email: '',
    phone: '',
    timezone: '',
    bio: ''
  });
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [privacySettings, setPrivacySettings] = useState({
    shareDisciples: false,
    shareJourney: false, // New: Share spiritual journey
    shareEmail: false,   // New: Share email with org members
    sharePhone: false,   // New: Share phone with org members
  });
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isEditingStory, setIsEditingStory] = useState(false);
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [expandedStoryId, setExpandedStoryId] = useState(null);
  const [visibleStories, setVisibleStories] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [invitations, setInvitations] = useState([]);
  const [firebaseDocId, setFirebaseDocId] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [organizationToLeave, setOrganizationToLeave] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [storyToEdit, setStoryToEdit] = useState(null);
  const [leaveError, setLeaveError] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const location = useLocation();
  const [journeyLoading, setJourneyLoading] = useState(true);
  const [journeyData, setJourneyData] = useState(null);
  const [journeyAnalysis, setJourneyAnalysis] = useState(null);

  // Add state for notification preferences
  const [notificationPreferences, setNotificationPreferences] = useState({
    push: {
      enabled: true,
      permission: 'default'  // browser notification permission status
    },
    fcm: {
      enabled: true  // Web browser FCM notifications
    },
    email: {
      enabled: false,
      verified: false  // tracks if email is verified
    },
    sms: {
      enabled: false,
      verified: false  // tracks if phone is verified
    },
    storyInteractionEmails: { // New setting for story interactions
      enabled: true 
    },
    quietHours: {
      enabled: true,
      start: "22:00",
      end: "08:00"
    }
  });

  const [openDrawer, setOpenDrawer] = useState(null); // 'stories', 'journey', 'details', 'alerts', 'privacy', 'orgs'
  const [editingStoryId, setEditingStoryId] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterOrgs, setFilterOrgs] = useState([]);
  const [storyPage, setStoryPage] = useState(1);
  const STORIES_PER_PAGE = 20;

  const [editingJourneySection, setEditingJourneySection] = useState(null);
  const [draftJourneyAnswers, setDraftJourneyAnswers] = useState({});

  const [showShareModal, setShowShareModal] = useState(false);
  const [storyToShare, setStoryToShare] = useState(null);

  // New state for story interactions
  const [expandedStoryInteractions, setExpandedStoryInteractions] = useState(null);
  const [interactions, setInteractions] = useState({});
  const [interactionsLoading, setInteractionsLoading] = useState(false);

  // Add this effect to handle the tab parameter (e.g. from Connect page "View your My Journey" link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'journey') {
      setSelectedTab(1); // Index 1 is the My Journey tab
      setOpenDrawer('journey'); // Open the My Journey drawer so the link lands with it visible
    }
  }, [location]);

  // Fetch user data: support both appID (e.g. UID003) and Firebase UID (doc id) in the URL
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        setError(null);

        let userDoc = null;
        let firebaseId = null;

        // Try lookup by appID first (shareable profile URLs)
        const userRef = collection(db, 'users');
        const q = query(userRef, where('appID', '==', userId));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          userDoc = querySnapshot.docs[0];
          firebaseId = userDoc.id;
        }

        // If not found by appID, treat URL param as Firebase document ID (e.g. from member list links)
        if (!userDoc) {
          const docRef = doc(db, 'users', userId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            firebaseId = docSnap.id;
            userDoc = { data: () => docSnap.data() };
          }
        }

        if (!userDoc) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const data = userDoc.data();
        setFirebaseDocId(firebaseId);

        const userOrgsRef = collection(db, 'users', firebaseId, 'organizations');
        const userOrgsSnap = await getDocs(userOrgsRef);
        const organizations = userOrgsSnap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));

        setUserData({
          ...data,
          id: firebaseId,
          appID: data.appID || userId,
          organizations
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  // Add protection for data access
  const isCurrentUser = currentUser && (
    currentUser.uid === userData?.uid || 
    currentUser.uid === userData?.id
  );

  useEffect(() => {
    if (userData) {
      setEditValues({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        timezone: userData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        bio: userData.bio || ''
      });
    }
  }, [userData]);

  const handleEdit = (field) => {
    if (!isCurrentUser) return; // Only allow editing own profile
    setIsEditing(prev => ({ ...prev, [field]: true }));
    setEditValues(prev => ({ ...prev, [field]: userData[field] || '' }));
  };

  const handleSave = async (field) => {
    try {
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        [field]: editValues[field]
      });

      setUserData(prev => ({
        ...prev,
        [field]: editValues[field]
      }));
      setIsEditing(prev => ({ ...prev, [field]: false }));

    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    }
  };

  const handleCancel = (field) => {
    setIsEditing(prev => ({ ...prev, [field]: false }));
    setEditValues(prev => ({
      ...prev,
      [field]: userData[field] || ''
    }));
  };

  const handleVerifyPhone = async (phoneNumber) => {
    try {
      setVerificationError(null);
      
      const formattedPhone = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+1${phoneNumber.replace(/\D/g, '')}`;
      
      console.log('Sending verification to:', formattedPhone);
      
      const response = await fetch('/api/send-verification', {  // Updated endpoint
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: formattedPhone }),
      });

      const data = await response.json();
      
      if (data.success) {
        setShowVerificationModal(true);
      } else {
        setVerificationError(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification:', error);
      setVerificationError('Failed to send verification code. Please try again.');
    }
  };

  const handleVerifyCode = async () => {
    try {
      setVerificationError(null);
      
      if (!verificationCode) {
        setVerificationError('Please enter the verification code');
        return;
      }
      
      let formattedPhone = editValues.phone;
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+1${formattedPhone.replace(/\D/g, '')}`;
      }
      
      const response = await fetch('/api/verify-code', {  // Updated endpoint
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          code: verificationCode.trim(),
        }),
      });

      const data = await response.json();
      
      if (data.success && data.valid) {
        await updateDoc(doc(db, 'users', userData.id), {
          phone: formattedPhone,
          phoneVerified: true
        });
        
        setUserData(prev => ({
          ...prev,
          phone: formattedPhone,
          phoneVerified: true
        }));
        
        setShowVerificationModal(false);
        setVerificationCode('');
      } else {
        setVerificationError('Invalid verification code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setVerificationError('Failed to verify code. Please try again.');
    }
  };

  const handlePrivacyUpdate = async (setting, value) => {
    try {
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        [`privacySettings.${setting}`]: value
      });
      
      setPrivacySettings(prev => ({
        ...prev,
        [setting]: value
      }));
    } catch (error) {
      console.error('Error updating privacy settings:', error);
    }
  };

  const fetchUserStories = async () => {
    try {
      setLoading(true);
      
      // Get stories from user's subcollection
      const storiesRef = collection(db, 'users', userData.id, 'stories');
      const storiesQuery = query(
        storiesRef,
        orderBy('createdAt', 'desc')
      );
      
      const storiesSnap = await getDocs(storiesQuery);
      const fetchedStories = storiesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('Fetched user stories:', fetchedStories); // Debug log
      setStories(fetchedStories);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Make sure we're calling it when userData changes
  useEffect(() => {
    if (!userData?.id) return;
    fetchUserStories();
  }, [userData?.id]);

  // Update story
  const handleUpdateStory = async (storyId, updatedData) => {
    try {
      // Update main story in user's collection
      const storyRef = doc(db, 'users', userId, 'stories', storyId);
      await updateDoc(storyRef, {
        ...updatedData,
        lastUpdated: serverTimestamp()
      });

      // Update organization references
      if (!updatedData.sharing.private) {
        // Remove from orgs no longer shared with
        const storySnap = await getDoc(storyRef);
        const oldOrgIds = storySnap.data()?.sharing?.organizations || [];
        const removedOrgIds = oldOrgIds.filter(id => !updatedData.sharing.organizations.includes(id));
        
        await Promise.all(removedOrgIds.map(orgId => 
          deleteDoc(doc(db, 'organizations', orgId, 'stories', storyId))
        ));

        // Update or add to current shared orgs
        const orgUpdates = updatedData.sharing.organizations.map(orgId => 
          setDoc(doc(db, 'organizations', orgId, 'stories', storyId), {
            storyId,
            authorId: userId,
            sharedAt: serverTimestamp(),
            visible: true,
            lastUpdated: serverTimestamp()
          }, { merge: true })
        );

        await Promise.all(orgUpdates);
      }

      // Update stories-feed if exists
      const feedRef = doc(db, 'stories-feed', storyId);
      const feedSnap = await getDoc(feedRef);
      if (feedSnap.exists()) {
        await updateDoc(feedRef, {
          preview: updatedData.text.substring(0, 200),
          lastUpdated: serverTimestamp()
        });
      }

      fetchUserStories(); // Refresh stories
    } catch (error) {
      console.error('Error updating story:', error);
    }
  };

  // Delete story
  const handleDeleteStory = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;

    try {
      // Delete main story
      const storyRef = doc(db, 'users', userId, 'stories', storyId);
      const storySnap = await getDoc(storyRef);
      const storyData = storySnap.data();

      // Delete org references
      if (!storyData.sharing.private) {
        const deleteOrgRefs = storyData.sharing.organizations.map(orgId =>
          deleteDoc(doc(db, 'organizations', orgId, 'stories', storyId))
        );
        await Promise.all(deleteOrgRefs);
      }

      // Delete from stories-feed
      await deleteDoc(doc(db, 'stories-feed', storyId));
      
      // Delete main story document
      await deleteDoc(storyRef);

      fetchUserStories(); // Refresh stories
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  };

  const handleJoinOrganization = async (orgId) => {
    if (!userData?.id) return;

    try {
      // Check if organization allows self-join
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      const orgData = orgDoc.data();

      if (orgData.settings.visibility !== 'public') {
        throw new Error('This organization is private');
      }

      const requireApproval = orgData.settings?.requireAdminApproval ?? 
                              (orgData.settings?.joinProcess === 'manual') ?? true;
      if (requireApproval) {
        // Create pending request (use userId as doc ID for consistency with approve flow)
        const requestRef = doc(db, 'organizations', orgId, 'membershipRequests', userData.id);
        await setDoc(requestRef, {
          userId: userData.id,
          email: userData.email,
          name: userData.name || 'Unknown User',
          requestedAt: serverTimestamp(),
          status: 'pending'
        });

        // Add to user's pending organizations
        const userOrgRef = doc(db, 'users', userData.id, 'organizations', orgId);
        await setDoc(userOrgRef, {
          status: 'pending',
          requestedAt: serverTimestamp()
        });
      } else {
        // Auto-join process
        await handleAcceptInvitation({ organizationId: orgId });
      }
    } catch (error) {
      console.error('Error joining organization:', error);
    }
  };

  const handleLeaveOrganization = async () => {
    try {
      if (!organizationToLeave) {
        console.log('No organization selected to leave');
        return;
      }
      
      console.log('Attempting to leave organization:', organizationToLeave);
      await leaveOrganization(organizationToLeave.id);
      
      // Update local state to show inactive status
      setOrganizations(orgs =>
        orgs.map(org =>
          org.id === organizationToLeave.id ? { ...org, status: 'inactive' } : org
        )
      );
      
      setShowLeaveModal(false);
      setOrganizationToLeave(null);
    } catch (error) {
      console.error('Error leaving organization:', error);
      setLeaveError(error.message);
    }
  };

  const leaveOrganization = async (organizationId) => {
    if (!currentUser?.uid) {
      throw new Error('No current user');
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userOrgRef = doc(userRef, 'organizations', organizationId);
      const userOrgDoc = await getDoc(userOrgRef);

      if (!userOrgDoc.exists()) {
        throw new Error("Membership information not found.");
      }
      const userOrgData = userOrgDoc.data();
      
      console.log('User org data:', userOrgData);

      if (userOrgData?.isCreator) {
        throw new Error("Organization creators cannot leave. Please delete the organization or transfer ownership first.");
      }

      // Mark membership as inactive in user's subcollection
      await updateDoc(userOrgRef, {
        status: 'inactive',
        leftAt: serverTimestamp()
      });

      // Remove from the activeOrgs array on the main user document
      await updateDoc(userRef, {
        activeOrgs: arrayRemove(organizationId)
      });
      
      // Also update the organization's members and users so admin list no longer shows them
      const orgMemberRef = doc(db, 'organizations', organizationId, 'members', currentUser.uid);
      const orgUserRef = doc(db, 'organizations', organizationId, 'users', currentUser.uid);
      const [orgMemberDoc, orgUserDoc] = await Promise.all([getDoc(orgMemberRef), getDoc(orgUserRef)]);
      const inactiveUpdate = { status: 'inactive', leftAt: serverTimestamp() };
      if (orgMemberDoc.exists()) await updateDoc(orgMemberRef, inactiveUpdate);
      if (orgUserDoc.exists()) await updateDoc(orgUserRef, inactiveUpdate);

      // Free the license seat so the org can allocate it to someone else
      const orgRef = doc(db, 'organizations', organizationId);
      const allocationRef = doc(db, 'organizations', organizationId, 'licenseAllocations', currentUser.uid);
      const allocationHistoryRef = collection(db, 'organizations', organizationId, 'licenseAllocationHistory');

      await runTransaction(db, async (transaction) => {
        const orgSnap = await transaction.get(orgRef);
        const allocationSnap = await transaction.get(allocationRef);
        const memberSnap = await transaction.get(orgMemberRef);

        if (!orgSnap.exists()) return;

        const orgData = orgSnap.data();
        const currentLicense = orgData?.license || {};
        const currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
        const currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;

        const hadAllocation = allocationSnap.exists() || memberSnap.data()?.licenseAllocated;
        if (!hadAllocation) return;

        const newUsed = Math.max(0, currentUsed - 1);
        const newAvailable = currentTotal - newUsed;

        transaction.update(orgRef, {
          'license.disciplemakersUsed': newUsed,
          'license.disciplemakersAvailable': newAvailable,
          'license.seatsUsed': newUsed,
          'license.seatsAvailable': newAvailable,
          'license.updatedAt': serverTimestamp(),
        });

        const historyDocRef = doc(allocationHistoryRef);
        transaction.set(historyDocRef, {
          userId: currentUser.uid,
          action: 'revoked',
          revokedBy: currentUser.uid,
          reason: 'member_left_org',
          organizationId,
          timestamp: serverTimestamp(),
        });

        if (allocationSnap.exists()) {
          transaction.update(allocationRef, {
            status: 'revoked',
            revokedAt: serverTimestamp(),
            revokedBy: currentUser.uid,
          });
        }

        if (orgMemberDoc.exists()) {
          transaction.update(orgMemberRef, {
            licenseAllocated: false,
            licenseStatus: 'revoked',
            licenseAllocatedAt: deleteField(),
            licenseExpiresAt: deleteField(),
          });
        }
      });

      return true;
    } catch (error) {
      console.error('Error in leaveOrganization:', error);
      throw error;
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      await updateDoc(doc(db, 'users', firebaseDocId), {
        [field]: value
      });
      
      setUserData(prev => ({
        ...prev,
        [field]: value
      }));
      
      setIsEditing(prev => ({ ...prev, [field]: false }));
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully`);
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error(`Failed to update ${field}`);
    }
  };

  // Add this near your other useEffects
  useEffect(() => {
    if (!userData?.id) return;

    // Set up real-time listener for user document
    const userRef = doc(db, 'users', userData.id);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        setUserData(prevData => ({
          ...prevData,
          ...doc.data()
        }));
      }
    }, (error) => {
      console.error('Real-time update error:', error);
    });

    return () => unsubscribe();
  }, [userData?.id]);

  // Update the fetch function to include status
  useEffect(() => {
    const fetchOrgs = async () => {
      if (!userData?.id) return;
      
      try {
        // First get user's organization memberships
        const userOrgsRef = collection(db, 'users', userData.id, 'organizations');
        const snapshot = await getDocs(userOrgsRef);
        
        // Then get each organization's details and combine them
        const orgs = await Promise.all(snapshot.docs.map(async userOrgDoc => {
          // Get the main organization data
          const orgRef = doc(db, 'organizations', userOrgDoc.id);
          const orgDoc = await getDoc(orgRef);
          const orgData = orgDoc.data();
          
          return {
            id: userOrgDoc.id,
            name: orgData?.name,
            status: orgData?.status,  // Don't default to 'active' anymore
            role: userOrgDoc.data()?.role,
            isCreator: userOrgDoc.data()?.isCreator
          };
        }));
        
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
  
    // Only run once when userData.id changes
    const cleanup = fetchOrgs();
    return () => cleanup;
  }, [userData?.id]); // Only depend on userData.id

  // First, let's define these missing functions
  const handleAcceptInvitation = async (inviteData) => {
    try {
      const { organizationId } = inviteData;
      const userRef = doc(db, 'users', userData.id);
      
      // Add to the user's organization subcollection
      const userOrgRef = doc(userRef, 'organizations', organizationId);
      await setDoc(userOrgRef, {
        role: 'Member',
        status: 'active',
        joinedAt: serverTimestamp()
      }, { merge: true });
      
      // Add to the activeOrgs array
      await updateDoc(userRef, {
        activeOrgs: arrayUnion(organizationId)
      });
      
      // Add to the organization's members subcollection
      const orgMemberRef = doc(db, 'organizations', organizationId, 'members', userData.id);
      await setDoc(orgMemberRef, {
        name: userData.name || 'Unknown User',
        email: userData.email,
        photoURL: userData.photoURL || '',
        status: 'active',
        role: 'Member',
        joinedAt: serverTimestamp()
      });
      
      // Refresh user data is handled by realtime listener, but we can force-refresh orgs
      // This will be reflected in the UI due to the onSnapshot listener for organizations
      setOrganizations(prevOrgs => [...prevOrgs, {
        id: organizationId,
        name: 'New Organization', // This will be updated by the listener
        status: 'active',
        role: 'Member'
      }]);

    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to join organization.');
    }
  };

  const handleDeclineInvitation = async (inviteData) => {
    try {
      const { organizationId } = inviteData;
      const userRef = doc(db, 'users', userData.id);
      
      // Remove the organization from pending invites
      await updateDoc(userRef, {
        [`organizations.${organizationId}`]: deleteField()
      });
      
      // Refresh user data
      const updatedDoc = await getDoc(userRef);
      setUserData(updatedDoc.data());
    } catch (error) {
      console.error('Error declining invitation:', error);
    }
  };

  // First, let's add console logging to see what data we have
  useEffect(() => {
    console.log('userData:', userData);
    console.log('organizationDetails:', organizations);
  }, [userData, organizations]);

  const handleCreateGroup = () => {
    console.log('Create group clicked');
    // Implement group creation logic here
  };

  // Add this logging wrapper for the ImageUpload component
  const handleImageUpload = async (url) => {
    console.log('Starting profile image upload...', {
      currentUser: currentUser?.uid,
      userId: userData?.id,
      imageUrl: url
    });

    try {
      // Update user document in Firestore first
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        photoURL: url
      });

      // Update local state
      setUserData(prev => ({
        ...prev,
        photoURL: url
      }));

      console.log('Profile image update successful');
      setUploadError(null);
    } catch (error) {
      console.error('Error updating profile image:', error);
      setUploadError('Failed to update profile image');
    }
  };

  const OrgsAndGroupsTab = () => {
    if (!firebaseDocId) return null;

    const isOwnProfile = currentUser.uid === firebaseDocId;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">My Communities</h3>
        </div>

        {organizations.length > 0 ? (
          <div className="bg-white p-4 rounded-lg shadow space-y-4">
            {organizations.map((org) => (
              <div key={org.id} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{org.name}</p>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      org.status === 'active' ? 'bg-green-100 text-green-800' :
                      org.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {org.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    {org.status !== 'inactive' && (
                      <a
                        href={`/organization/${org.id}`}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        {(org.role === 'Admin' || org.isCreator) ? 'Manage' : 'View'}
                      </a>
                    )}
                    {org.status !== 'inactive' && (
                      <button
                        onClick={() => {
                          setOrganizationToLeave(org);
                          setShowLeaveModal(true);
                        }}
                        className="text-gray-500 hover:text-gray-700 text-sm"
                      >
                        Leave
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isOwnProfile && (
              <button
                onClick={() => navigate('/discover-communities')}
                className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Join Another Community
              </button>
            )}
          </div>
        ) : (
          isOwnProfile && (
            <div className="text-center bg-white p-8 rounded-lg shadow">
              <p className="text-gray-600">You are not a member of any communities yet.</p>
              <button
                onClick={() => navigate('/discover-communities')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Find a Community to Join
              </button>
            </div>
          )
        )}
      </div>
    );
  }

  // Add this effect to fetch spiritual journey data
  useEffect(() => {
    const fetchSpiritualJourney = async () => {
      if (!userId) return;
      
      try {
        const journeyRef = doc(db, 'users', firebaseDocId, 'spiritualJourney', 'main');
        const journeySnap = await getDoc(journeyRef);
        
        if (journeySnap.exists()) {
          const journeyData = journeySnap.data();
          setUserData(prevData => ({
            ...prevData,
            spiritualJourney: { main: journeyData }
          }));
          console.log('Fetched journey data:', journeyData);
        }
      } catch (error) {
        console.error('Error fetching spiritual journey:', error);
      }
    };

    if (firebaseDocId) {
      fetchSpiritualJourney();
    }
  }, [userId, firebaseDocId]);

  // Add this effect to fetch journey data
  useEffect(() => {
    const fetchJourneyData = async () => {
      if (!firebaseDocId) return;
      
      try {
        setJourneyLoading(true);
        const journeyRef = doc(db, 'users', firebaseDocId, 'spiritualJourney', 'main');
        
        // Set up real-time listener
        const unsubscribe = onSnapshot(journeyRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            setJourneyData(data);
            
            // Trigger analysis when we get new journey data
            if (data.responses) {
              const analysis = analyzeJourneyResponses(data.responses);
              setJourneyAnalysis(analysis);
              console.log('Journey Analysis:', analysis); // For debugging
            }
          } else {
            setJourneyData(null);
            setJourneyAnalysis(null);
          }
          setJourneyLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching journey data:', error);
        setJourneyLoading(false);
      }
    };

    fetchJourneyData();
  }, [firebaseDocId]);

  // Add this effect near the top of the component
  useEffect(() => {
    if (userData?.notificationPreferences) {
      setNotificationPreferences(userData.notificationPreferences);
    }
  }, [userData]);

  // Add this function inside the User component
  const saveNotificationPreferences = async (newPreferences) => {
    if (!userData?.id) return;
    
    try {
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        notificationPreferences: newPreferences
      });
      toast.success('Notification preferences updated');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast.error('Failed to save preferences');
      // Revert the UI state if save fails
      setNotificationPreferences(userData.notificationPreferences);
    }
  };

  // Filtered stories logic
  const filteredStories = stories.filter(story => {
    // Date filter
    let passDate = true;
    if (filterStartDate) {
      const created = story.createdAt?.toDate ? story.createdAt.toDate() : new Date(story.createdAt?.seconds ? story.createdAt.seconds * 1000 : story.createdAt);
      passDate = passDate && created >= filterStartDate;
    }
    if (filterEndDate) {
      const created = story.createdAt?.toDate ? story.createdAt.toDate() : new Date(story.createdAt?.seconds ? story.createdAt.seconds * 1000 : story.createdAt);
      passDate = passDate && created <= filterEndDate;
    }
    // Org filter
    let passOrg = true;
    if (filterOrgs.length > 0) {
      passOrg = Array.isArray(story.sharing?.organizations) && story.sharing.organizations.some(orgId => filterOrgs.includes(orgId));
    }
    return passDate && passOrg;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredStories.length / STORIES_PER_PAGE);
  const paginatedStories = filteredStories.slice((storyPage - 1) * STORIES_PER_PAGE, storyPage * STORIES_PER_PAGE);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-background min-h-screen">
        <div className="flex justify-center items-center">
          <div className="text-lg text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-6 bg-background min-h-screen">
        <div className="flex justify-center items-center">
          <div className="text-lg">No user data found</div>
        </div>
      </div>
    );
  }

  // Only show edit controls if it's the current user
  const showEditControls = isCurrentUser;

  // New function to fetch interactions for a specific story
  const fetchInteractionsForStory = async (storyId) => {
    if (!firebaseDocId) return;
    setInteractionsLoading(true);
    try {
      const likesRef = collection(db, 'users', firebaseDocId, 'stories', storyId, 'likes');
      const commentsRef = collection(db, 'users', firebaseDocId, 'stories', storyId, 'comments');
      const prayersRef = collection(db, 'users', firebaseDocId, 'stories', storyId, 'prayers');

      const [likesSnap, commentsSnap, prayersSnap] = await Promise.all([
        getDocs(likesRef),
        getDocs(commentsRef),
        getDocs(prayersRef)
      ]);

      const likes = likesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'like' }));
      const comments = commentsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'comment' }));
      const prayers = prayersSnap.docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'prayer' }));

      const allInteractions = [...likes, ...comments, ...prayers];
      
      allInteractions.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA; // Sort descending, newest first
      });

      setInteractions(prev => ({
        ...prev,
        [storyId]: allInteractions
      }));
    } catch (error)      {
      console.error("Error fetching story interactions:", error);
      toast.error("Failed to load story activity.");
    } finally {
      setInteractionsLoading(false);
    }
  };

  // New function to toggle the interaction feed's visibility
  const toggleInteractions = (storyId) => {
    const isOpening = expandedStoryInteractions !== storyId;
    setExpandedStoryInteractions(isOpening ? storyId : null);

    if (isOpening && !interactions[storyId]) {
      fetchInteractionsForStory(storyId);
    }
  };

  // New function to handle deleting a comment
  const handleDeleteComment = async (storyId, commentId) => {
    if (!firebaseDocId) return;
    if (!window.confirm("Are you sure you want to delete this comment?")) return;

    try {
      const commentRef = doc(db, 'users', firebaseDocId, 'stories', storyId, 'comments', commentId);
      await deleteDoc(commentRef);
      toast.success("Comment deleted.");
      
      // Refresh interactions for this story
      fetchInteractionsForStory(storyId);

    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment.");
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      {/* Profile Image, Name, Bio */}
      <div className="flex flex-col items-center mb-6">
        <img
          src={userData?.photoURL || '/default-avatar.png'}
          alt={userData?.name || 'Profile'}
          className="w-28 h-28 rounded-full object-cover border-4 border-blue-200 shadow mb-2"
        />
        <h2 className="text-2xl font-bold text-center">{userData?.name}</h2>
        <p className="text-gray-600 text-center mt-2">{userData?.bio}</p>
      </div>

      {/* List of Profile Sections */}
      <div className="bg-white rounded-xl shadow divide-y divide-gray-200">
        <button
          className="w-full text-left px-6 py-4 hover:bg-blue-50 focus:outline-none"
          onClick={() => setOpenDrawer('details')}
        >
          <span className="font-medium text-gray-800">Edit Details</span>
          <span className="block text-gray-400 text-sm">Name, email, phone, etc.</span>
        </button>
        <button
          className="w-full text-left px-6 py-4 hover:bg-blue-50 focus:outline-none"
          onClick={() => setOpenDrawer('stories')}
        >
          <span className="font-medium text-gray-800">Stories</span>
          <span className="block text-gray-400 text-sm">Your shared stories</span>
        </button>
        <button
          className="w-full text-left px-6 py-4 hover:bg-blue-50 focus:outline-none"
          onClick={() => setOpenDrawer('journey')}
        >
          <span className="font-medium text-gray-800">My Journey</span>
          <span className="block text-gray-400 text-sm">Spiritual journey & milestones</span>
        </button>
        <button
          className="w-full text-left px-6 py-4 hover:bg-blue-50 focus:outline-none"
          onClick={() => setOpenDrawer('alerts')}
        >
          <span className="font-medium text-gray-800">Alerts</span>
          <span className="block text-gray-400 text-sm">Notification & reminder settings</span>
        </button>
        <button
          className="w-full text-left px-6 py-4 hover:bg-blue-50 focus:outline-none"
          onClick={() => setOpenDrawer('privacy')}
        >
          <span className="font-medium text-gray-800">Privacy</span>
          <span className="block text-gray-400 text-sm">Profile & story privacy</span>
        </button>
        <button
          className="w-full text-left px-6 py-4 hover:bg-blue-50 focus:outline-none"
          onClick={() => setOpenDrawer('orgs')}
        >
          <span className="font-medium text-gray-800">Organizations</span>
          <span className="block text-gray-400 text-sm">Memberships & roles</span>
        </button>
      </div>

      {/* Drawers for each section */}
      <Drawer
        open={openDrawer === 'details'}
        onClose={() => setOpenDrawer(null)}
        title="Edit Details"
        subtitle="Name, email, phone, etc."
        avatar={userData?.photoURL}
      >
        <div className="space-y-6 max-w-md mx-auto overflow-y-auto max-h-[80vh] pb-24">
          {/* Profile Image Upload */}
          {showEditControls && (
            <div className="flex flex-col items-center">
              <div className="transform scale-75">
                <ImageUpload
                  currentImage={userData?.photoURL}
                  onUpload={handleImageUpload}
                  folder={`users/${userData?.id}`}
                  label="Change Profile Photo"
                />
              </div>
              {uploadError && <div className="text-red-500 text-sm -mt-4 mb-2">{uploadError}</div>}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 p-2.5 bg-gray-50/50">
              {isEditing.name ? (
                <>
                  <input
                    type="text"
                    className="flex-grow bg-transparent focus:outline-none text-gray-900"
                    value={editValues.name}
                    onChange={e => setEditValues(v => ({ ...v, name: e.target.value }))}
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <button onClick={() => handleSave('name')} className="text-green-500 hover:text-green-700"><CheckIcon className="w-5 h-5" /></button>
                    <button onClick={() => handleCancel('name')} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-gray-800">{userData?.name}</span>
                  {showEditControls && <button onClick={() => handleEdit('name')} className="text-gray-400 hover:text-gray-600"><PencilIcon className="w-4 h-4" /></button>}
                </>
              )}
            </div>
          </div>
          
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 p-2.5 bg-gray-50/50">
                {isEditing.email ? (
                    <>
                        <input
                            type="email"
                            className="flex-grow bg-transparent focus:outline-none text-gray-900"
                            value={editValues.email}
                            onChange={e => setEditValues(v => ({ ...v, email: e.target.value }))}
                            autoFocus
                        />
                        <div className="flex gap-3">
                            <button onClick={() => handleSave('email')} className="text-green-500 hover:text-green-700"><CheckIcon className="w-5 h-5" /></button>
                            <button onClick={() => handleCancel('email')} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="text-gray-800">{userData?.email}</span>
                        {showEditControls && <button onClick={() => handleEdit('email')} className="text-gray-400 hover:text-gray-600"><PencilIcon className="w-4 h-4" /></button>}
                    </>
                )}
            </div>
          </div>
          
          {/* Phone */}
           <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 p-2.5 bg-gray-50/50">
                  {isEditing.phone ? (
                      <>
                          <input
                              type="tel"
                              className="flex-grow bg-transparent focus:outline-none text-gray-900"
                              value={editValues.phone}
                              onChange={e => setEditValues(v => ({ ...v, phone: e.target.value }))}
                              autoFocus
                          />
                          <div className="flex gap-3">
                              <button onClick={() => handleSave('phone')} className="text-green-500 hover:text-green-700"><CheckIcon className="w-5 h-5" /></button>
                              <button onClick={() => handleCancel('phone')} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
                          </div>
                      </>
                  ) : (
                      <>
                          <span className="text-gray-800">{userData?.phone}</span>
                          {showEditControls && <button onClick={() => handleEdit('phone')} className="text-gray-400 hover:text-gray-600"><PencilIcon className="w-4 h-4" /></button>}
                      </>
                  )}
              </div>
              {userData?.phone && (
                <div className="mt-2 text-xs flex items-center gap-2">
                    {userData.phoneVerified ? (
                        <span className="text-green-600 flex items-center gap-1"><ShieldCheckIcon className="w-4 h-4" /> Verified</span>
                    ) : (
                        <>
                            <button onClick={() => handleVerifyPhone(editValues.phone)} className="text-blue-600 hover:underline">
                                Verify Phone
                            </button>
                            {phoneError && <span className="text-red-500">{phoneError}</span>}
                        </>
                    )}
                </div>
              )}
            </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Timezone</label>
            <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 p-2.5 bg-gray-50/50">
                {isEditing.timezone ? (
                    <div className="flex-grow">
                        <TimezoneSelect
                            value={editValues.timezone}
                            onChange={tz => setEditValues(v => ({ ...v, timezone: tz.value }))}
                        />
                    </div>
                ) : (
                    <span className="text-gray-800">{userData?.timezone}</span>
                )}
                {isEditing.timezone ? (
                    <div className="flex gap-3 pl-3">
                        <button onClick={() => handleSave('timezone')} className="text-green-500 hover:text-green-700"><CheckIcon className="w-5 h-5" /></button>
                        <button onClick={() => handleCancel('timezone')} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
                    </div>
                ) : (
                    showEditControls && <button onClick={() => handleEdit('timezone')} className="text-gray-400 hover:text-gray-600"><PencilIcon className="w-4 h-4" /></button>
                )}
            </div>
          </div>

          {/* Bio */}
          <div>
              <label className="block text-sm font-medium text-gray-700">Bio</label>
              <div className="mt-1 rounded-md border border-gray-200 p-2.5 bg-gray-50/50">
                  {isEditing.bio ? (
                      <>
                          <textarea
                              className="w-full bg-transparent focus:outline-none min-h-[80px] text-gray-900"
                              value={editValues.bio}
                              onChange={e => setEditValues(v => ({ ...v, bio: e.target.value }))}
                              autoFocus
                          />
                          <div className="flex gap-3 justify-end">
                              <button onClick={() => handleSave('bio')} className="text-green-500 hover:text-green-700"><CheckIcon className="w-5 h-5" /></button>
                              <button onClick={() => handleCancel('bio')} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
                          </div>
                      </>
                  ) : (
                      <div className="flex items-start justify-between">
                          <p className="text-gray-800 whitespace-pre-wrap">{userData?.bio || <span className="text-gray-400">Not set</span>}</p>
                          {showEditControls && <button onClick={() => handleEdit('bio')} className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-4"><PencilIcon className="w-4 h-4" /></button>}
                      </div>
                  )}
              </div>
          </div>
        </div>
      </Drawer>
      <Drawer open={openDrawer === 'stories'} onClose={() => setOpenDrawer(null)} title="Stories" subtitle="Your shared stories" avatar={userData?.photoURL}>
        {/* Filter Bar */}
        <div className="border-b border-gray-200 px-2 py-1 flex items-center justify-between cursor-pointer bg-gray-50" onClick={() => setShowFilter(v => !v)}>
          <span className="flex items-center gap-1 text-sm text-gray-700 font-medium"><FunnelIcon className="w-4 h-4" /> Filter</span>
          {showFilter ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
        </div>
        {showFilter && (
          <div className="p-3 bg-gray-50 border-b border-gray-200 animate-fade-in">
            <div className="flex flex-col gap-3">
              {/* Date Range */}
              <div className="flex gap-2 items-center">
                <label className="text-xs text-gray-600">From</label>
                <input type="date" className="input input-sm input-bordered" value={filterStartDate ? filterStartDate.toISOString().slice(0,10) : ''} onChange={e => setFilterStartDate(e.target.value ? new Date(e.target.value) : null)} />
                <label className="text-xs text-gray-600">To</label>
                <input type="date" className="input input-sm input-bordered" value={filterEndDate ? filterEndDate.toISOString().slice(0,10) : ''} onChange={e => setFilterEndDate(e.target.value ? new Date(e.target.value) : null)} />
              </div>
              {/* Organization Multi-select */}
              <div className="flex gap-2 items-center">
                <label className="text-xs text-gray-600">Organization</label>
                <select multiple className="input input-sm input-bordered min-w-[120px]" value={filterOrgs} onChange={e => setFilterOrgs(Array.from(e.target.selectedOptions, o => o.value))}>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <button className="btn btn-xs btn-outline" onClick={() => { setFilterStartDate(null); setFilterEndDate(null); setFilterOrgs([]); }}>Clear</button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-4 max-w-md mx-auto overflow-y-auto max-h-[80vh]">
          {filteredStories.length === 0 ? (
            <p className="text-gray-500 text-center">No stories found for selected filters.</p>
          ) : (
            paginatedStories.map(story => (
              <div key={story.id} className="p-4 border rounded-lg hover:bg-gray-50 relative transition-all duration-300">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-lg">{story.title}</h4>
                    <p className="text-gray-600 mt-1">{story.text.substring(0, 100)}...</p>
                    {/* Video Content */}
                    {story.playableURL && (
                      <div className="mt-3">
                        <div className="bg-gray-100 rounded-lg overflow-hidden">
                          <video
                            className="w-full h-full object-cover rounded-lg"
                            controls
                            preload="metadata"
                            src={story.playableURL}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    )}
                    {story.lastUpdated && story.lastUpdated.seconds !== story.createdAt?.seconds ? (
                      <p className="text-sm text-gray-500 mt-1">Edited: {formatDate(story.lastUpdated)}</p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-1">Created: {formatDate(story.createdAt)}</p>
                    )}
                  </div>
                  {showEditControls && (
                    <div className="flex gap-2">
                      <button onClick={() => { console.log('Edit clicked for story:', story.id, story); setEditingStoryId(story.id); }} className="btn btn-ghost btn-xs"><PencilIcon className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteStory(story.id)} className="btn btn-ghost btn-xs"><TrashIcon className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
                {/* Shared/Private flag */}
                <div className="absolute bottom-2 right-4">
                  {Array.isArray(story.sharing?.organizations) && story.sharing.organizations.length > 0 ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Shared</span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">Private</span>
                  )}
                </div>
                
                {/* Activity Feed Toggle */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <button onClick={() => toggleInteractions(story.id)} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <span>Activity Feed</span>
                        {expandedStoryInteractions === story.id ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                    </button>
                </div>

                {/* Interaction Feed */}
                {expandedStoryInteractions === story.id && (
                    <div className="mt-3 space-y-3 animate-fade-in">
                        {interactionsLoading ? (
                           <p className="text-gray-500 text-sm">Loading activity...</p>
                        ) : !interactions[story.id] || interactions[story.id].length === 0 ? (
                            <p className="text-gray-500 text-sm">No activity yet.</p>
                        ) : (
                          interactions[story.id]?.map(interaction => (
                              <div key={interaction.id} className="flex items-start gap-3 text-sm">
                                  {/* Icon */}
                                  <div className="mt-1 flex-shrink-0">
                                      {interaction.type === 'comment' && <ChatIcon className="w-4 h-4 text-gray-500" />}
                                      {interaction.type === 'like' && <HeartIcon className="w-4 h-4 text-red-500" />}
                                      {interaction.type === 'prayer' && <UserGroupIcon className="w-4 h-4 text-blue-500" />}
                                  </div>
                                  {/* Content */}
                                  <div className="flex-1">
                                      <p className="text-gray-800 leading-relaxed">
                                          <span className="font-semibold">{interaction.userName || 'Someone'}</span>
                                          {interaction.type === 'comment' && <> commented: <span className="text-gray-600">"{interaction.text}"</span></>}
                                          {interaction.type === 'like' && ' liked this story.'}
                                          {interaction.type === 'prayer' && ' prayed for this story.'}
                                      </p>
                                      <p className="text-xs text-gray-400 mt-0.5">
                                          {formatDate(interaction.createdAt)}
                                      </p>
                                  </div>
                                  {/* Delete Button for story owner on comments */}
                                  {interaction.type === 'comment' && isCurrentUser && (
                                      <button onClick={() => handleDeleteComment(story.id, interaction.id)} className="text-gray-400 hover:text-red-600 flex-shrink-0">
                                          <TrashIcon className="w-4 h-4" />
                                      </button>
                                  )}
                              </div>
                          ))
                        )}
                    </div>
                )}
                
                {editingStoryId === story.id && (
                  <div className="mt-4">
                    <EditStory
                      story={story}
                      onClose={() => {
                        setEditingStoryId(null);
                        fetchUserStories();
                      }}
                      currentUser={currentUser}
                    />
                  </div>
                )}
              </div>
            ))
          )}
          {/* Pagination Controls */}
          {filteredStories.length > STORIES_PER_PAGE && (
            <div className="flex justify-center items-center gap-4 mt-4">
              <button
                className="btn btn-xs btn-outline"
                disabled={storyPage === 1}
                onClick={() => setStoryPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {storyPage} of {totalPages}</span>
              <button
                className="btn btn-xs btn-outline"
                disabled={storyPage === totalPages}
                onClick={() => setStoryPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
          <div className="pb-24" />
        </div>
      </Drawer>
      <Drawer open={openDrawer === 'journey'} onClose={() => setOpenDrawer(null)} title="My Journey" subtitle="Spiritual journey & milestones" avatar={userData?.photoURL}>
        <div className="space-y-4 max-w-md mx-auto overflow-y-auto max-h-[80vh] pb-24">
          {journeyLoading ? (
            <div className="flex justify-center items-center py-8 text-gray-400">Loading journey...</div>
          ) : !journeyData || !journeyData.responses ? (
            <div className="text-center text-gray-500 py-8">No journey responses found.</div>
          ) : (
            <>
              {/* Journey Responses by Section */}
              <div className="space-y-6">
                {sections.map((section, sIdx) => {
                  const isEditing = editingJourneySection === sIdx;
                  const sectionAnswers = journeyData.responses || {};
                  return (
                    <div key={section.title} className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-2 flex items-center justify-between">
                        {section.title}
                        {!isEditing && (
                          <button className="btn btn-xs btn-outline ml-2" onClick={() => {
                            setEditingJourneySection(sIdx);
                            // Load current answers for this section into draft state
                            const draft = {};
                            section.questions.forEach((q, qIdx) => {
                              draft[`${sIdx}_${qIdx}`] = sectionAnswers[`${sIdx}_${qIdx}`] || '';
                            });
                            setDraftJourneyAnswers(draft);
                          }}>
                            Edit
                          </button>
                        )}
                      </h3>
                      <ul className="space-y-2">
                        {section.questions.map((q, qIdx) => {
                          const key = `${sIdx}_${qIdx}`;
                          if (isEditing) {
                            return (
                              <li key={q}>
                                <span className="block text-sm text-gray-700 font-medium">{q}</span>
                                <textarea
                                  className="input input-bordered w-full mt-1"
                                  rows={5}
                                  value={draftJourneyAnswers[key] || ''}
                                  onChange={e => setDraftJourneyAnswers(draft => ({ ...draft, [key]: e.target.value }))}
                                />
                              </li>
                            );
                          } else {
                            const answer = sectionAnswers[key];
                            return answer && answer.trim() ? (
                              <li key={q}>
                                <span className="block text-sm text-gray-700 font-medium">{q}</span>
                                <span className="block text-gray-600 ml-2">{answer}</span>
                              </li>
                            ) : null;
                          }
                        })}
                      </ul>
                      {isEditing && (
                        <div className="flex gap-2 mt-4">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={async () => {
                              // Save only the edited section's answers
                              const updatedResponses = {
                                ...journeyData.responses,
                                ...draftJourneyAnswers
                              };
                              const journeyRef = doc(db, 'users', firebaseDocId, 'spiritualJourney', 'main');
                              await updateDoc(journeyRef, {
                                responses: updatedResponses,
                                lastUpdated: serverTimestamp()
                              });
                              setEditingJourneySection(null);
                            }}
                          >Save</button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setEditingJourneySection(null)}
                          >Cancel</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Journey Analysis */}
              {journeyAnalysis && (
                <div className="bg-white rounded-lg p-4 shadow mt-6">
                  <h3 className="font-semibold text-lg mb-2">Journey Analysis</h3>
                  {journeyAnalysis.dominantThemes && journeyAnalysis.dominantThemes.length > 0 && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Dominant Themes:</span>
                      <ul className="list-disc ml-6 text-gray-600">
                        {journeyAnalysis.dominantThemes.map((theme, idx) => (
                          <li key={idx}>
                            {typeof theme === 'string' ? theme : theme?.name || theme?.area || theme?.topic || JSON.stringify(theme)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {journeyAnalysis.primaryGrowthAreas && journeyAnalysis.primaryGrowthAreas.length > 0 && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Primary Growth Areas:</span>
                      <ul className="list-disc ml-6 text-gray-600">
                        {journeyAnalysis.primaryGrowthAreas.map((area, idx) => (
                          <li key={idx}>
                            {typeof area === 'string' ? area : area?.name || area?.area || area?.topic || JSON.stringify(area)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {journeyAnalysis.keyDevelopmentTopics && journeyAnalysis.keyDevelopmentTopics.length > 0 && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Key Development Topics:</span>
                      <ul className="list-disc ml-6 text-gray-600">
                        {journeyAnalysis.keyDevelopmentTopics.map((topic, idx) => (
                          <li key={idx}>
                            {typeof topic === 'string' ? topic : topic?.name || topic?.area || topic?.topic || JSON.stringify(topic)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Drawer>
      <Drawer open={openDrawer === 'alerts'} onClose={() => setOpenDrawer(null)} title="Alerts" subtitle="Notification & reminder settings" avatar={userData?.photoURL}>
        <div className="space-y-4 max-w-md mx-auto overflow-y-auto max-h-[80vh] pb-24">
          <h3 className="font-semibold text-lg mb-2"></h3>
          <NotificationPreferencesEditor
            notificationPreferences={notificationPreferences}
            onSave={saveNotificationPreferences}
          />
        </div>
      </Drawer>
      <Drawer open={openDrawer === 'privacy'} onClose={() => setOpenDrawer(null)} title="Privacy" subtitle="Profile & story privacy" avatar={userData?.photoURL}>
        <div className="space-y-4 max-w-md mx-auto overflow-y-auto max-h-[80vh] pb-24">
          <h3 className="font-semibold text-lg mb-2"></h3>
          <PrivacySettingsEditor
            privacySettings={privacySettings}
            onSave={async (draft) => {
              await Promise.all([
                handlePrivacyUpdate('shareDisciples', draft.shareDisciples),
                handlePrivacyUpdate('shareJourney', draft.shareJourney),
                handlePrivacyUpdate('shareEmail', draft.shareEmail),
                handlePrivacyUpdate('sharePhone', draft.sharePhone),
              ]);
            }}
          />
        </div>
      </Drawer>
      <Drawer open={openDrawer === 'orgs'} onClose={() => setOpenDrawer(null)} title="Organizations" subtitle="Memberships & roles" avatar={userData?.photoURL}>
        <div className="space-y-4 max-w-md mx-auto overflow-y-auto max-h-[80vh] pb-24">
          <h3 className="font-semibold text-lg mb-2"></h3>
          {organizations?.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No organizations found.</div>
          ) : (
            organizations.map((org) => (
              <div key={org.id} className="p-4 border rounded-lg hover:bg-gray-50 flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-lg">{org.name}</h4>
                      {org.status === 'pending' && (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>
                      )}
                      {org.status === 'inactive' && (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Inactive</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">{org.role}</span>
                      {org.isCreator && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="text-blue-600">Creator</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {org.status !== 'inactive' && (
                      <a
                        href={`/organization/${org.id}`}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        {(org.role === 'Admin' || org.isCreator) ? 'Manage' : 'View'}
                      </a>
                    )}
                    {org.status !== 'inactive' && (
                      <button
                        onClick={() => {
                          setOrganizationToLeave(org);
                          setShowLeaveModal(true);
                        }}
                        className="text-gray-500 hover:text-gray-700 text-sm"
                      >
                        Leave
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => navigate('/discover-communities')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-indigo-700"
            >
              Discover Communities
            </button>
            <button
              onClick={() => navigate('/create-organization')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Create Organization
            </button>
          </div>
          {/* Leave Organization Modal */}
          {showLeaveModal && organizationToLeave && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-lg">
                <h4 className="text-lg font-semibold mb-2">Leave Organization</h4>
                <p className="mb-4">Are you sure you want to leave <span className="font-bold">{organizationToLeave.name}</span>?</p>
                {leaveError && <div className="text-red-500 text-sm mb-2">{leaveError}</div>}
                <div className="flex gap-2 justify-end">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setShowLeaveModal(false)}
                  >Cancel</button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleLeaveOrganization}
                  >Leave</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {showShareModal && storyToShare && (
        <ShareStory
          story={storyToShare}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

// Add NotificationPreferencesEditor component at the bottom of the file
function NotificationPreferencesEditor({ notificationPreferences, onSave }) {
  // Ensure defaults for all notification preference fields
  const defaultPreferences = {
    push: { enabled: true },
    fcm: { enabled: true },
    email: { enabled: true, verified: false },
    sms: { enabled: false, verified: false },
    storyInteractionEmails: { enabled: true },
    quietHours: { enabled: true, start: '22:00', end: '08:00' },
    ...notificationPreferences
  };
  
  const [draft, setDraft] = React.useState(defaultPreferences);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    const merged = {
      push: { enabled: true, ...notificationPreferences?.push },
      fcm: { enabled: true, ...notificationPreferences?.fcm },
      email: { enabled: true, verified: false, ...notificationPreferences?.email },
      sms: { enabled: false, verified: false, ...notificationPreferences?.sms },
      storyInteractionEmails: { enabled: true, ...notificationPreferences?.storyInteractionEmails },
      quietHours: { enabled: true, start: '22:00', end: '08:00', ...notificationPreferences?.quietHours },
      ...notificationPreferences
    };
    setDraft(merged);
  }, [notificationPreferences]);

  const handleToggle = (type) => {
    setDraft(prev => {
      const currentValue = prev[type]?.enabled ?? true;
      return {
        ...prev,
        [type]: { ...(prev[type] || {}), enabled: !currentValue }
      };
    });
    setEditing(true);
  };
  
  const handleQuietHoursToggle = () => {
    setDraft(prev => ({
      ...prev,
      quietHours: {
        ...(prev.quietHours || { enabled: true, start: '22:00', end: '08:00' }),
        enabled: !(prev.quietHours?.enabled ?? true)
      }
    }));
    setEditing(true);
  };

  const handleQuietHoursChange = (field, value) => {
    setDraft(prev => ({
      ...prev,
      quietHours: {
        ...(prev.quietHours || { enabled: true, start: '22:00', end: '08:00' }),
        [field]: value
      }
    }));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      ...draft,
      preferredReminderTime: draft.preferredReminderTime || "09:00"
    });
    setSaving(false);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(notificationPreferences);
    setEditing(false);
  };

  return (
    <div className="space-y-4">
      {/* Mobile Push Notifications */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <span className="text-gray-700">Mobile Push Notifications</span>
          <div className="text-gray-400 text-xs">Receive notifications on your mobile device</div>
        </div>
        <Switch
          checked={draft.push?.enabled ?? true}
          onChange={() => handleToggle('push')}
          className={`${draft.push?.enabled ?? true ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Enable Mobile Push Notifications</span>
          <span
            className={`${draft.push?.enabled ?? true ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Web Browser FCM Notifications */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <span className="text-gray-700">Web Browser Notifications</span>
          <div className="text-gray-400 text-xs">Receive notifications in your web browser when logged in</div>
        </div>
        <Switch
          checked={draft.fcm?.enabled ?? true}
          onChange={() => handleToggle('fcm')}
          className={`${draft.fcm?.enabled ?? true ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Enable Web Browser Notifications</span>
          <span
            className={`${draft.fcm?.enabled ?? true ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Email Notifications */}
      <div className="flex items-center justify-between">
        <span className="text-gray-700">Email Notifications {draft.email?.verified && <span className="text-green-600 text-xs ml-1">(Verified)</span>}</span>
        <Switch
          checked={draft.email?.enabled ?? true}
          onChange={() => handleToggle('email')}
          className={`${draft.email?.enabled ?? true ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Enable Email Notifications</span>
          <span
            className={`${draft.email?.enabled ?? true ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* SMS Notifications */}
      <div className="flex items-center justify-between">
        <span className="text-gray-700">SMS Notifications {draft.sms?.verified && <span className="text-green-600 text-xs ml-1">(Verified)</span>}</span>
        <Switch
          checked={draft.sms?.enabled ?? false}
          onChange={() => handleToggle('sms')}
          className={`${draft.sms?.enabled ?? false ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Enable SMS Notifications</span>
          <span
            className={`${draft.sms?.enabled ?? false ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Story Interaction Emails */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <span className="text-gray-700">Story Interaction Emails</span>
          <div className="text-gray-400 text-xs">Get notified when someone likes, prays for, or comments on your stories</div>
        </div>
        <Switch
          checked={draft.storyInteractionEmails?.enabled ?? true}
          onChange={() => handleToggle('storyInteractionEmails')}
          className={`${draft.storyInteractionEmails?.enabled ?? true ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Enable Story Interaction Emails</span>
          <span
            className={`${draft.storyInteractionEmails?.enabled ?? true ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Quiet Hours */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Quiet Hours</span>
          <Switch
            checked={draft.quietHours?.enabled ?? true}
            onChange={handleQuietHoursToggle}
            className={`${draft.quietHours?.enabled ?? true ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
          >
            <span className="sr-only">Enable Quiet Hours</span>
            <span
              className={`${draft.quietHours?.enabled ?? true ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
        {draft.quietHours?.enabled && (
          <div className="flex gap-4 items-center p-3">
            <label className="text-xs text-gray-600">From</label>
            <input
              type="time"
              className="input input-sm input-bordered"
              value={draft.quietHours?.start || '22:00'}
              onChange={e => handleQuietHoursChange('start', e.target.value)}
            />
            <label className="text-xs text-gray-600">To</label>
            <input
              type="time"
              className="input input-sm input-bordered"
              value={draft.quietHours?.end || '08:00'}
              onChange={e => handleQuietHoursChange('end', e.target.value)}
            />
          </div>
        )}
      </div>
      
      {/* Default Reminder Time with Tooltip */}
      <div className="flex items-center justify-between">
        <span className="text-gray-700 flex items-center gap-2">
          Default Reminder Time
          <span className="relative group">
            <button
              type="button"
              className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold focus:outline-none"
              tabIndex={0}
              aria-label="Info about default reminder time"
            >
              i
            </button>
            <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block group-focus:block bg-white border border-gray-300 rounded shadow-lg px-3 py-2 text-xs text-gray-700 w-64">
              This time may be overridden with a specific time for each disciple, but this is the default time for notifications unless you are more specific.
            </span>
          </span>
        </span>
        <input
          type="time"
          value={draft.preferredReminderTime || "09:00"}
          onChange={e => {
            setDraft(prev => ({ ...prev, preferredReminderTime: e.target.value }));
            setEditing(true);
          }}
          className="input input-sm input-bordered"
        />
      </div>
      
      {/* Save/Cancel Buttons */}
      {editing && (
        <div className="flex gap-2 justify-end mt-4">
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button className="btn btn-sm btn-outline" onClick={handleCancel} disabled={saving}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// Add PrivacySettingsEditor component at the bottom of the file
function PrivacySettingsEditor({ privacySettings, onSave }) {
  const [draft, setDraft] = React.useState(privacySettings);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setDraft(privacySettings);
  }, [privacySettings]);

  const handleToggle = (field) => {
    setDraft(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft);
    setEditing(false);
    setSaving(false);
  };

  const handleCancel = () => {
    setDraft(privacySettings);
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Share My Disciples */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-gray-700 font-medium">List My Disciples</div>
          <div className="text-gray-400 text-sm">Names are shared with my orgs</div>
        </div>
        <Switch
          checked={draft.shareDisciples}
          onChange={() => handleToggle('shareDisciples')}
          className={`${draft.shareDisciples ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Share My Disciples</span>
          <span
            className={`${draft.shareDisciples ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Share My Journey */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-gray-700 font-medium">Share My Journey</div>
          <div className="text-gray-400 text-sm">Make my spiritual story public</div>
        </div>
        <Switch
          checked={draft.shareJourney}
          onChange={() => handleToggle('shareJourney')}
          className={`${draft.shareJourney ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Share My Journey</span>
          <span
            className={`${draft.shareJourney ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Share My Email */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-gray-700 font-medium">Share My Email</div>
          <div className="text-gray-400 text-sm">Only with members in my orgs</div>
        </div>
        <Switch
          checked={draft.shareEmail}
          onChange={() => handleToggle('shareEmail')}
          className={`${draft.shareEmail ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Share My Email</span>
          <span
            className={`${draft.shareEmail ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {/* Share My Phone */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-gray-700 font-medium">Share My Phone</div>
          <div className="text-gray-400 text-sm">Only with members in my orgs</div>
        </div>
        <Switch
          checked={draft.sharePhone}
          onChange={() => handleToggle('sharePhone')}
          className={`${draft.sharePhone ? 'bg-blue-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
        >
          <span className="sr-only">Share My Phone</span>
          <span
            className={`${draft.sharePhone ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      {editing && (
        <div className="flex gap-2 justify-end mt-4">
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button className="btn btn-sm btn-outline" onClick={handleCancel} disabled={saving}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export default User;